import { ActionPayloadData, DataRow, EventAction } from 'plugins/types';
import { displayGraphRedirectProps } from './utils';

enum EVENT_ACTION_CATEGORY {
  AGENT_ACTIONS = 'Agent Actions',
  ENDPOINT_ACTIONS = 'Endpoint Actions',
  RESPONSE = 'Response',
  AGENT_UPGRADE = 'Agent Upgrade',
  SHORTCUTS = 'Shortcuts',
  FULL_DISK_SCAN = 'Full Disk Scan',
  TROUBLESHOOTING = 'Troubleshooting',
  CONFIGURATION = 'Configuration',
  TECH_SUPPORT = 'Tech Support',
  MITIGATION = 'Mitigation',
  LINKS = 'Links',
}

// Not every EVENT_ACTION_CATEGORY is here, like MITIGATION
export const endpointActionsCategories = [
  EVENT_ACTION_CATEGORY.AGENT_ACTIONS,
  EVENT_ACTION_CATEGORY.ENDPOINT_ACTIONS,
  EVENT_ACTION_CATEGORY.RESPONSE,
  EVENT_ACTION_CATEGORY.AGENT_UPGRADE,
  EVENT_ACTION_CATEGORY.SHORTCUTS,
  EVENT_ACTION_CATEGORY.FULL_DISK_SCAN,
  EVENT_ACTION_CATEGORY.TROUBLESHOOTING,
  EVENT_ACTION_CATEGORY.CONFIGURATION,
  EVENT_ACTION_CATEGORY.TECH_SUPPORT,
];

const SUPPORT_ACTION_PATH = 'private/agents/support-actions';
const BASIC_ACTION_PATH = 'agents/actions';
const THREATS_ACTION_PATH = 'threats';
const ENDPOINTS_PATH_BASE = 'sentinels/devices';
const PROCESS_GRAPH_PATH = 'dv/process-summary-report';

export const DEFAULT_ACTION_SUCCESS_MESSAGE = 'Action completed successfully';

const decommissionAgentAction: EventAction = {
  label: 'Decommission',
  actionId: 'decommission',
  category: EVENT_ACTION_CATEGORY.AGENT_ACTIONS,
  actionPath: BASIC_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'id', fieldName: 'ids' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `${computerName} will be decommissioned.<br/> Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Initiated decommission on ${countAffected} Endpoints.`,
};

const duplicateUUIDAgentAction: EventAction = {
  label: 'Randomize UUID',
  actionName: 'randomizeUUID',
  actionId: 'randomize-uuid',
  payloadConfig: [{ fieldKey: 'id', fieldName: 'ids' }],
  category: EVENT_ACTION_CATEGORY.AGENT_ACTIONS,
  actionPath: BASIC_ACTION_PATH,
  getConfirmationMessage: () =>
    'This action will assign a new UUID to this endpoint. Run this command only when instructed to do so by SentinelOne support. <br/><br/>' +
    'Historical threat and Deep-visibility data will be kept, yet disassociated from this endpoint.',
  getSuccessMessage: (rowData, countAffected) => `Initiated randomize UUID on ${countAffected} Endpoints.`,
};

const disconnectFromNetworkAgentAction: EventAction = {
  label: 'Disconnect from Network',
  actionName: 'disconnectFromNetwork',
  actionId: 'disconnect',
  category: EVENT_ACTION_CATEGORY.RESPONSE,
  actionPath: BASIC_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'id', fieldName: 'ids' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `The remote Endpoint ${computerName} will be disconnected from network with no user warning.<br/>Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Started to disconnect ${countAffected} Endpoints.`,
};

const reconnectToNetworkAgentAction: EventAction = {
  label: 'Reconnect to Network',
  actionName: 'reconnectToNetwork',
  actionId: 'connect',
  category: EVENT_ACTION_CATEGORY.RESPONSE,
  actionPath: BASIC_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `${computerName} will reconnect to the network.<br/> Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Reconnecting ${countAffected} Endpoints.`,
};

const fetchLogAgentAction: EventAction = {
  label: 'Fetch Logs',
  actionId: 'fetchLogs',
  category: EVENT_ACTION_CATEGORY.TROUBLESHOOTING,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getSuccessMessage: (rowData, countAffected) => `Getting logs from ${countAffected} Endpoints.`,
};

const disableAgentAction: EventAction = {
  label: 'Disable Agent',
  actionId: 'disableAgent',
  category: EVENT_ACTION_CATEGORY.TROUBLESHOOTING,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getSuccessMessage: (rowData, countAffected) => `Disable Agent initiated successfully on ${countAffected} Agents`,
};

const enableAgentAction: EventAction = {
  label: 'Enable Agent',
  actionId: 'enableAgent',
  category: EVENT_ACTION_CATEGORY.TROUBLESHOOTING,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

const updateSoftwareAgentAction: EventAction = {
  label: 'Update Agent',
  actionId: 'updateSoftware',
  category: EVENT_ACTION_CATEGORY.AGENT_UPGRADE,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'id', fieldName: 'ids' }],
  getSuccessMessage: (rowData, countAffected) => `The upgrade was sent to ${countAffected} Endpoints.`,
};

const fileFetchAgentAction: EventAction = {
  label: 'File Fetch',
  actionName: 'fileFetch',
  actionId: 'fetchFiles',
  category: EVENT_ACTION_CATEGORY.RESPONSE,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

const initiateScanAgentAction: EventAction = {
  label: 'Initiate Scan',
  actionId: 'initiate-scan',
  actionName: 'initiateScan',
  category: EVENT_ACTION_CATEGORY.FULL_DISK_SCAN,
  actionPath: BASIC_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `${computerName} will be scanned.<br/> Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Started scan on ${countAffected} Endpoints.`,
};

const abortScanAgentAction: EventAction = {
  label: 'Abort Scan',
  actionId: 'abort-scan',
  actionName: 'abortScan',
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  category: EVENT_ACTION_CATEGORY.FULL_DISK_SCAN,
  actionPath: BASIC_ACTION_PATH,
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `This will stop the scan of ${computerName}.<br/> Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Stopping scan on ${countAffected} Endpoints.`,
};

const sentMessageAgentAction: EventAction = {
  label: 'Send Message',
  actionName: 'sendMessage',
  actionId: 'broadcast',
  category: EVENT_ACTION_CATEGORY.ENDPOINT_ACTIONS,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `A message will be sent to ${computerName}.<br/> Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `The message was sent to ${countAffected} Endpoints.`,
};

const editCustomerIdentifierAgentAction: EventAction = {
  label: 'Edit Customer Identifier',
  actionName: 'setCustomerIdentifier',
  actionId: 'setExternalId',
  category: EVENT_ACTION_CATEGORY.AGENT_ACTIONS,
  // noScope: true,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

const rebootAgentAction: EventAction = {
  label: 'Reboot',
  actionName: 'reboot',
  actionId: 'restart-machine',
  category: EVENT_ACTION_CATEGORY.ENDPOINT_ACTIONS,
  actionPath: BASIC_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'id', fieldName: 'ids' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `${computerName} will be rebooted.<br/> Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Rebooting ${countAffected} Endpoints.`,
};

const showPassphraseAgentAction: EventAction = {
  label: 'Show Passphrase',
  actionId: 'passphrases',
  actionName: 'showPassphrase',
  category: EVENT_ACTION_CATEGORY.AGENT_ACTIONS,
  payloadConfig: [{ fieldKey: 'id', fieldName: 'ids' }],
  redirectPath: ENDPOINTS_PATH_BASE,
};

const shutdownAgentAction: EventAction = {
  label: 'Shut Down',
  actionId: 'shutdown',
  actionName: 'shutDown',
  payloadConfig: [{ fieldKey: 'id', fieldName: 'ids' }],
  category: EVENT_ACTION_CATEGORY.ENDPOINT_ACTIONS,
  actionPath: BASIC_ACTION_PATH,
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `${computerName} will shutdown with no user warning.<br/> Do you want to continue?`;
  },
  // disableWhenInactive: true,
  getSuccessMessage: (rowData, countAffected) => `Shutting down ${countAffected} Endpoints.`,
};

const uninstallAgentAction: EventAction = {
  label: 'Uninstall',
  actionId: 'uninstall',
  category: EVENT_ACTION_CATEGORY.ENDPOINT_ACTIONS,
  actionPath: BASIC_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'id', fieldName: 'ids' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `Are you sure you want to uninstall the Endpoint '${computerName}'?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Uninstall was sent to ${countAffected} Endpoints`,
};

const approveUninstallAgentAction: EventAction = {
  label: 'Approve Uninstall',
  actionId: 'approve-uninstall',
  category: EVENT_ACTION_CATEGORY.ENDPOINT_ACTIONS,
  actionPath: BASIC_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `${computerName} will be uninstalled.<br/> Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Approved uninstall for ${countAffected} Endpoints.`,
};

const rejectUninstall: EventAction = {
  label: 'Reject Uninstall',
  actionName: 'rejectUninstall',
  actionId: 'reject-uninstall',
  category: EVENT_ACTION_CATEGORY.ENDPOINT_ACTIONS,
  actionPath: BASIC_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `${computerName} will not be uninstalled.<br/> Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Rejected uninstall for ${countAffected} Endpoints.`,
};

const viewThreatsAgentAction: EventAction = {
  label: 'View Threats',
  category: EVENT_ACTION_CATEGORY.SHORTCUTS,
  redirectPath: 'incidents/threats',
  getParams: (rowData: DataRow) => {
    const filter = { uuid__contains: rowData['agent.uuid'], timeTitle: 'Last 30 Days' };
    return `?filter=${JSON.stringify(filter)}`;
  },
};

const viewAppsAgentAction: EventAction = {
  label: 'Show Applications',
  category: EVENT_ACTION_CATEGORY.SHORTCUTS,
  redirectPath: '/applications',
  getParams: (rowData: DataRow) => {
    const filter = { agentUuid__contains: rowData['agent.uuid'] };
    return `?filter=${JSON.stringify(filter)}`;
  },
};

const searchOnDV: EventAction = {
  label: 'Search in Singularity XDR',
  actionId: 'searchInDV',
  category: EVENT_ACTION_CATEGORY.SHORTCUTS,
  actionHandler: (rowData, redirectCallback) => {
    if (redirectCallback) {
      const agentUuid = rowData['agent.uuid'];
      const filter = `agent.uuid = '${agentUuid}'`;

      redirectCallback({ filter, view: 'edr' });
    }
  },
};

const purgeResearchData: EventAction = {
  label: 'Purge Research Data',
  actionName: 'purgeResearchData',
  actionId: 'purge-research-data',
  category: EVENT_ACTION_CATEGORY.TECH_SUPPORT,
  actionPath: SUPPORT_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getConfirmationMessage: () =>
    'Purge Research Data will delete all research data of this Endpoint.<br/>Do you want to continue?',
  getSuccessMessage: (rowData, countAffected) => `Purge Research Data sent to ${countAffected} Endpoints.`,
};

const purgeCrashDumps: EventAction = {
  label: 'Purge Crash Dumps',
  actionId: 'purge-crash-dumps',
  category: EVENT_ACTION_CATEGORY.TECH_SUPPORT,
  actionPath: SUPPORT_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `Purge Crash Dumps will delete all existing crash dumps of ${computerName}.<br /> Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Purge Crash Dumps sent to ${countAffected} Endpoints.`,
};

const flushEventsQueue: EventAction = {
  label: 'Flush Event Queue',
  actionId: 'flush-events-queue',
  category: EVENT_ACTION_CATEGORY.TECH_SUPPORT,
  actionPath: SUPPORT_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `Flush Event Queue will remove all unsent os events and threats from ${computerName}.<br /> Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Flush Event Queue sent to ${countAffected} Endpoints.`,
};

const resetConfiguration: EventAction = {
  label: 'Reset Local Configuration',
  actionName: 'resetLocalConfiguration',
  actionId: 'reset-local-config',
  category: EVENT_ACTION_CATEGORY.CONFIGURATION,
  actionPath: BASIC_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `Reset configuration will affect ${computerName}.<br /> Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Reset configuration sent to ${countAffected} Endpoints.`,
};

const restartServices: EventAction = {
  label: 'Restart Services',
  actionId: 'restart',
  actionName: 'restartServices',
  actionPath: SUPPORT_ACTION_PATH,
  category: EVENT_ACTION_CATEGORY.TROUBLESHOOTING,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `Restart will affect ${computerName}.<br /> Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Restart action was sent to ${countAffected} Endpoints.`,
};

const protectAgentAction: EventAction = {
  label: 'Protect',
  actionId: 'protect',
  category: EVENT_ACTION_CATEGORY.TROUBLESHOOTING,
  actionPath: SUPPORT_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `Protect will affect ${computerName}.<br /> Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Protect action was sent to ${countAffected} Endpoints.`,
};

const unprotectAgentAction: EventAction = {
  label: 'Unprotect',
  actionId: 'unprotect',
  category: EVENT_ACTION_CATEGORY.TROUBLESHOOTING,
  actionPath: SUPPORT_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `Unprotect will affect ${computerName}.<br /> Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Unprotect action was sent to ${countAffected} Endpoints.`,
};

const markAgentAsUpToDate: EventAction = {
  label: 'Mark as Up To Date',
  actionId: 'mark-up-to-date',
  actionName: 'markAsUpToDate',
  category: EVENT_ACTION_CATEGORY.AGENT_UPGRADE,
  actionPath: BASIC_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `Mark as Up To Date will affect ${computerName}.<br /> Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Mark as Up To Date action was sent to ${countAffected} Endpoints.`,
};

const revokeTokenAction: EventAction = {
  label: 'Revoke Token',
  actionName: 'revokeToken',
  actionId: 'revoke-token',
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  category: EVENT_ACTION_CATEGORY.AGENT_ACTIONS,
  actionPath: SUPPORT_ACTION_PATH,
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `Revoke token will affect ${computerName}.<br /> Do you want to continue?`;
  },
  getSuccessMessage: (rowData, countAffected) => `Revoke token action was sent to ${countAffected} Endpoints.`,
};

const reloadWindowsAction: EventAction = {
  label: 'Reload',
  actionId: 'reload',
  actionName: 'reloadConf',
  category: EVENT_ACTION_CATEGORY.TROUBLESHOOTING,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

const purgeDBAction: EventAction = {
  label: 'Purge Database',
  actionId: 'purgeDB',
  category: EVENT_ACTION_CATEGORY.TECH_SUPPORT,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

const sendCrushDumps: EventAction = {
  label: 'Control Crash Dumps',
  actionId: 'controlCrashDumps',
  category: EVENT_ACTION_CATEGORY.TECH_SUPPORT,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

const sendResearchData: EventAction = {
  label: 'Control Research Data',
  actionId: 'controlResearchData',
  category: EVENT_ACTION_CATEGORY.TECH_SUPPORT,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

const eventThrottling: EventAction = {
  label: 'Event Throttling',
  actionId: 'eventsThrottling',
  category: EVENT_ACTION_CATEGORY.TECH_SUPPORT,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

const configurationAction: EventAction = {
  label: 'Agent Configuration',
  actionId: 'setConfig',
  actionName: 'configuration',
  category: EVENT_ACTION_CATEGORY.CONFIGURATION,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

const reportFWConfigurationAction: EventAction = {
  label: 'Configure Firewall Logging',
  actionId: 'configureFirewallLogging',
  category: EVENT_ACTION_CATEGORY.CONFIGURATION,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

const migrateAgentsAction: EventAction = {
  label: 'Migrate Agent',
  actionId: 'migrateAgent',
  category: EVENT_ACTION_CATEGORY.AGENT_ACTIONS,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

const moveAgentToSiteAgentAction: EventAction = {
  label: 'Move to Another Site',
  actionId: 'moveToAnotherSite',
  category: EVENT_ACTION_CATEGORY.AGENT_ACTIONS,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

const openRemoteShellAgentAction: EventAction = {
  label: 'Remote Shell',
  actionId: 'remoteShell',
  category: EVENT_ACTION_CATEGORY.RESPONSE,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

const clearRemoteShellSession: EventAction = {
  label: 'Clear Remote Shell Session',
  actionId: 'clear-remote-shell-session',
  actionName: 'clearRemoteShellSession',
  category: EVENT_ACTION_CATEGORY.RESPONSE,
  actionPath: BASIC_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getConfirmationMessage: () => 'You are about to clear Remote Shell session',
  getSuccessMessage: () => 'Clear Remote Shell Session Succeed.',
};

const enableRangerAction: EventAction = {
  label: 'Enable Ranger',
  actionId: 'ranger-enable',
  actionName: 'enableRanger',
  category: EVENT_ACTION_CATEGORY.CONFIGURATION,
  actionPath: BASIC_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `This will enable Ranger functionality on ${computerName}.<br/>Are you sure?`;
  },
  getSuccessMessage: (rowData, countAffected) => `The action was sent to ${countAffected} Endpoints.`,
};

const disableRangerAction: EventAction = {
  label: 'Disable Ranger',
  actionName: 'ranger-disable',
  actionId: 'disableRanger',
  category: EVENT_ACTION_CATEGORY.CONFIGURATION,
  actionPath: BASIC_ACTION_PATH,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
  getConfirmationMessage: (rowData) => {
    const computerName = rowData['endpoint.name'];
    return `This will disable Ranger functionality on ${computerName}.<br/>Are you sure?`;
  },
  getSuccessMessage: (rowData, countAffected) => `The action was sent to ${countAffected} Endpoints.`,
};

const startRemoteProfilerAction: EventAction = {
  label: 'Start Remote Profiler',
  actionId: 'startProfiling',
  category: EVENT_ACTION_CATEGORY.TECH_SUPPORT,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

const stopRemoteProfilerAction: EventAction = {
  label: 'Stop Remote Profiler',
  actionId: 'stopProfiling',
  category: EVENT_ACTION_CATEGORY.TECH_SUPPORT,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

const runScriptAction: EventAction = {
  label: 'Run Script',
  actionId: 'runScript',
  category: EVENT_ACTION_CATEGORY.RESPONSE,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

export const manageTagsAction: EventAction = {
  label: 'Manage Tags',
  actionId: 'manageTags',
  category: EVENT_ACTION_CATEGORY.AGENT_ACTIONS,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

const approveStatelessUpgradeAction: EventAction = {
  label: 'Approve Local Upgrade',
  actionId: 'approveLocalUpgrade',
  actionName: 'approveStatelessUpgrade',
  category: EVENT_ACTION_CATEGORY.AGENT_ACTIONS,
  redirectPath: ENDPOINTS_PATH_BASE,
  payloadConfig: [{ fieldKey: 'agent.uuid', fieldName: 'uuid' }],
};

export const saveAsRuleAction: EventAction = {
  label: 'Create STAR Rule',
  actionId: 'saveAsRule',
  actionName: 'saveAsRule',
  category: EVENT_ACTION_CATEGORY.LINKS,
  redirectPath: 'sentinels/custom-rules',
  payloadConfig: [{ fieldKey: 'query', fieldName: 'query' }],
};

const markAsThreat: EventAction = {
  label: 'Mark As Threat',
  actionId: 'dv-mark-as-threat',
  actionName: 'markAsThreatActionDV',
  category: EVENT_ACTION_CATEGORY.MITIGATION,
  actionPath: THREATS_ACTION_PATH,
  getConfirmationMessage: () =>
    'This creates alerts for all associated objects and applies the policies of the Agents. <br/> If the policy of the Agent is Threat-Protect, Mark as Threat mitigates the objects.',
  getPayload: (data) => ({
    data: {
      events: [
        {
          agentId: data.id,
          storyline: data['src.process.storyline.id'],
        },
      ],
      initiatedBy: 'dvManual',
      status: 'threat',
    },
    filter: {},
  }),
  getSuccessMessage: (rowData, countAffected) => `Action was successful on ${countAffected} threat`,
};

const markAsSuspicious: EventAction = {
  label: 'Mark As Suspicious',
  actionId: 'dv-mark-as-threat',
  actionName: 'markAsSuspiciousActionDV',
  category: EVENT_ACTION_CATEGORY.MITIGATION,
  getConfirmationMessage: () =>
    'This creates alerts for all associated objects and applies the policies of the Agents. <br/> If the policy of the Agent is Suspicious-Protect, Mark as Suspicious mitigates the objects.',
  actionPath: THREATS_ACTION_PATH,
  getPayload: (data) => ({
    data: {
      events: [
        {
          agentId: data.id,
          storyline: data['src.process.storyline.id'],
        },
      ],
      initiatedBy: 'dvManual',
      status: 'suspicious',
    },
    filter: {},
  }),
  getSuccessMessage: (rowData, countAffected) => `Action was successful on ${countAffected} threat`,
};

const srcProcPayload = (dataRow: DataRow): ActionPayloadData => ({
  agentUuid: dataRow['agent.uuid'] as string,
  originProcessStartTime: dataRow['src.process.startTime'] as string,
  hierarchy: 'src',
  originProcessUniqueId: dataRow['src.process.uid'] as string,
  processGroupId: dataRow['src.process.storyline.id'] as string,
  eventStartTime: dataRow['event.time'] as string,
  eventId: dataRow['event.id'] as string,
});

const srcProcParentPayload = (dataRow: DataRow): ActionPayloadData => ({
  agentUuid: dataRow['agent.uuid'] as string,
  originProcessStartTime: dataRow['src.process.startTime'] as string,
  hierarchy: 'parent',
  originProcessUniqueId: dataRow['src.process.parent.uid'] as string,
  processGroupId: dataRow['src.process.parent.storyline.id'] as string,
  eventStartTime: dataRow['event.time'] as string,
  eventId: dataRow['event.id'] as string,
});
const tgtProcPayload = (dataRow: DataRow): ActionPayloadData => ({
  agentUuid: dataRow['agent.uuid'] as string,
  originProcessStartTime: dataRow['tgt.process.startTime'] as string,
  hierarchy: 'tgt',
  originProcessUniqueId: dataRow['tgt.process.uid'] as string,
  processGroupId: dataRow['tgt.process.storyline.id'] as string,
  eventStartTime: dataRow['event.time'] as string,
  eventId: dataRow['event.id'] as string,
});

const viewProcessGraph: EventAction = {
  label: 'View Process Graph',
  actionId: 'process-graph',
  category: EVENT_ACTION_CATEGORY.SHORTCUTS,
  redirectPath: PROCESS_GRAPH_PATH,
  icon: 'launch',
  displayAction: displayGraphRedirectProps,
};

export const viewProcessGraphSrcProc = { ...viewProcessGraph, getPayload: srcProcPayload };
export const viewProcessGraphSrcProcParent = { ...viewProcessGraph, getPayload: srcProcParentPayload };
export const viewProcessGraphTgtProc = { ...viewProcessGraph, getPayload: tgtProcPayload };

export const shortcutsActions: EventAction[] = [viewThreatsAgentAction, viewAppsAgentAction, searchOnDV];

export const mitigationActions: EventAction[] = [markAsThreat, markAsSuspicious];

export const agentsActions: EventAction[] = [
  decommissionAgentAction,
  duplicateUUIDAgentAction,
  disconnectFromNetworkAgentAction,
  reconnectToNetworkAgentAction,
  fetchLogAgentAction,
  updateSoftwareAgentAction,
  fileFetchAgentAction,
  initiateScanAgentAction,
  abortScanAgentAction,
  sentMessageAgentAction,
  disableAgentAction,
  enableAgentAction,
  editCustomerIdentifierAgentAction,
  rebootAgentAction,
  showPassphraseAgentAction,
  shutdownAgentAction,
  uninstallAgentAction,
  approveUninstallAgentAction,
  rejectUninstall,
  resetConfiguration,
  markAgentAsUpToDate,
  configurationAction,
  reportFWConfigurationAction,
  migrateAgentsAction,
  moveAgentToSiteAgentAction,
  openRemoteShellAgentAction,
  clearRemoteShellSession,
  purgeResearchData,
  purgeCrashDumps,
  flushEventsQueue,
  restartServices,
  protectAgentAction,
  unprotectAgentAction,
  revokeTokenAction,
  reloadWindowsAction,
  purgeDBAction,
  sendCrushDumps,
  sendResearchData,
  eventThrottling,
  enableRangerAction,
  disableRangerAction,
  startRemoteProfilerAction,
  stopRemoteProfilerAction,
  runScriptAction,
  manageTagsAction,
  approveStatelessUpgradeAction,
];
